import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Settings,
  Save,
  RefreshCw,
  MapPin,
  Sun,
  Moon,
  Coffee,
  Utensils,
} from "lucide-react";
import {
  getWeekSchedule,
  updateWeekSchedule,
  getWeekTypes,
  getTeacherZone,
  updateTeacherZone,
  getAcademicZones,
} from "../../lib/api";

const DAYS_FULL = [
  { id: "lundi", label: "Lundi" },
  { id: "mardi", label: "Mardi" },
  { id: "mercredi", label: "Mercredi" },
  { id: "jeudi", label: "Jeudi" },
  { id: "vendredi", label: "Vendredi" },
  { id: "samedi", label: "Samedi" },
  { id: "dimanche", label: "Dimanche" },
];

const CYCLES = [
  { id: "cycle1", label: "Cycle 1 (Maternelle)" },
  { id: "cycle2", label: "Cycle 2 (CP-CE2)" },
  { id: "cycle3", label: "Cycle 3 (CM1-6e)" },
];

const NIVEAUX_BY_CYCLE = {
  cycle1: [
    { id: "ps", label: "PS" },
    { id: "ms", label: "MS" },
    { id: "gs", label: "GS" },
  ],
  cycle2: [
    { id: "cp", label: "CP" },
    { id: "ce1", label: "CE1" },
    { id: "ce2", label: "CE2" },
  ],
  cycle3: [
    { id: "cm1", label: "CM1" },
    { id: "cm2", label: "CM2" },
    { id: "6e", label: "6e" },
  ],
};

export default function WeekScheduleConfigDialog({ open, onOpenChange, onConfigSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("rythme");
  
  // Week schedule config
  const [weekTypes, setWeekTypes] = useState([]);
  const [config, setConfig] = useState({
    week_type: "4_jours",
    working_days: ["lundi", "mardi", "jeudi", "vendredi"],
    has_wednesday_morning: false,
    has_saturday_morning: false,
    morning_start: "08:30",
    morning_end: "11:45",
    morning_break_start: "10:15",
    morning_break_end: "10:30",
    afternoon_start: "13:30",
    afternoon_end: "16:15",
    afternoon_break_start: "15:00",
    afternoon_break_end: "15:15",
    half_day_start: "08:30",
    half_day_end: "11:30",
    half_day_break_start: "10:15",
    half_day_break_end: "10:30",
    grid_interval: 15, // Time grid interval in minutes (1-60)
  });

  // Teacher profile
  const [zones, setZones] = useState([]);
  const [profile, setProfile] = useState({
    zone: "B",
    cycles: ["cycle2"], // Changed from 'cycle' to 'cycles' (array)
    niveaux: ["cp", "ce1"],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [weekTypesRes, weekScheduleRes, zonesRes, profileRes] = await Promise.all([
        getWeekTypes(),
        getWeekSchedule(),
        getAcademicZones(),
        getTeacherZone(),
      ]);

      setWeekTypes(weekTypesRes.data || []);
      if (weekScheduleRes.data) {
        setConfig(prev => ({ ...prev, ...weekScheduleRes.data }));
      }
      setZones(zonesRes.data || []);
      if (profileRes.data) {
        // Handle migration from old 'cycle' to new 'cycles'
        const oldCycle = profileRes.data.cycle;
        const newCycles = profileRes.data.cycles || (oldCycle ? [oldCycle] : ["cycle2"]);
        setProfile({
          zone: profileRes.data.zone || "B",
          cycles: newCycles,
          niveaux: profileRes.data.niveaux || ["cp", "ce1"],
        });
      }
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Erreur lors du chargement de la configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const handleWeekTypeChange = (type) => {
    const weekType = weekTypes.find(wt => wt.id === type);
    if (weekType) {
      setConfig(prev => ({
        ...prev,
        week_type: type,
        working_days: weekType.days.filter(d => !d.includes("_matin")),
        has_wednesday_morning: weekType.has_wednesday_morning,
      }));
    }
  };

  const toggleWorkingDay = (day) => {
    setConfig(prev => {
      const days = prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day];
      return { ...prev, working_days: days, week_type: "custom" };
    });
  };

  const toggleNiveau = (niveau) => {
    setProfile(prev => {
      const niveaux = prev.niveaux.includes(niveau)
        ? prev.niveaux.filter(n => n !== niveau)
        : [...prev.niveaux, niveau];
      return { ...prev, niveaux };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updateWeekSchedule(config),
        updateTeacherZone({
          academic_zone: profile.zone,
          cycles: profile.cycles,
          niveaux: profile.niveaux,
        }),
      ]);
      toast.success("Configuration enregistrée");
      // Notify parent to refresh
      if (onConfigSaved) {
        onConfigSaved(config);
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const TimeInput = ({ label, value, onChange, icon: Icon }) => (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <Label className="text-sm text-slate-600 w-24">{label}</Label>
      <Input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-28 h-8 text-sm"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-500" />
            Configuration du temps scolaire
          </DialogTitle>
          <DialogDescription>
            Paramétrez le rythme de votre semaine, vos horaires et votre zone académique
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="rythme">
                <Calendar className="w-4 h-4 mr-2" />
                Rythme
              </TabsTrigger>
              <TabsTrigger value="horaires">
                <Clock className="w-4 h-4 mr-2" />
                Horaires
              </TabsTrigger>
              <TabsTrigger value="profil">
                <MapPin className="w-4 h-4 mr-2" />
                Profil
              </TabsTrigger>
            </TabsList>

            {/* Tab: Rythme */}
            <TabsContent value="rythme" className="space-y-6">
              {/* Week type selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Type de semaine</Label>
                <div className="grid grid-cols-2 gap-2">
                  {weekTypes.map((type) => (
                    <Button
                      key={type.id}
                      variant={config.week_type === type.id ? "default" : "outline"}
                      className="justify-start h-auto py-3"
                      onClick={() => handleWeekTypeChange(type.id)}
                    >
                      <div className="text-left">
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs opacity-70">
                          {type.id === "custom" 
                            ? "Configuration personnalisée"
                            : `${type.days.length} jours travaillés`}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Working days */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Jours travaillés</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_FULL.map((day) => (
                    <Button
                      key={day.id}
                      variant={config.working_days.includes(day.id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleWorkingDay(day.id)}
                      className="min-w-20"
                    >
                      {day.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Half days */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Demi-journées</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                    <span className="text-sm">Mercredi matin travaillé</span>
                    <Switch
                      checked={config.has_wednesday_morning}
                      onCheckedChange={(checked) =>
                        setConfig(prev => ({ ...prev, has_wednesday_morning: checked }))
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                    <span className="text-sm">Samedi matin travaillé</span>
                    <Switch
                      checked={config.has_saturday_morning}
                      onCheckedChange={(checked) =>
                        setConfig(prev => ({ ...prev, has_saturday_morning: checked }))
                      }
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab: Horaires */}
            <TabsContent value="horaires" className="space-y-6">
              {/* Morning */}
              <div className="space-y-3 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 font-semibold text-amber-800">
                  <Sun className="w-4 h-4" />
                  Matin
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <TimeInput
                    label="Début"
                    value={config.morning_start}
                    onChange={(v) => setConfig(prev => ({ ...prev, morning_start: v }))}
                    icon={Clock}
                  />
                  <TimeInput
                    label="Fin"
                    value={config.morning_end}
                    onChange={(v) => setConfig(prev => ({ ...prev, morning_end: v }))}
                    icon={Clock}
                  />
                </div>
                <div className="border-t border-amber-200 pt-3 mt-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-700 mb-2">
                    <Coffee className="w-4 h-4" />
                    Récréation matin
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <TimeInput
                      label="Début"
                      value={config.morning_break_start}
                      onChange={(v) => setConfig(prev => ({ ...prev, morning_break_start: v }))}
                      icon={Clock}
                    />
                    <TimeInput
                      label="Fin"
                      value={config.morning_break_end}
                      onChange={(v) => setConfig(prev => ({ ...prev, morning_break_end: v }))}
                      icon={Clock}
                    />
                  </div>
                </div>
              </div>

              {/* Afternoon */}
              <div className="space-y-3 bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                <div className="flex items-center gap-2 font-semibold text-indigo-800">
                  <Moon className="w-4 h-4" />
                  Après-midi
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <TimeInput
                    label="Début"
                    value={config.afternoon_start}
                    onChange={(v) => setConfig(prev => ({ ...prev, afternoon_start: v }))}
                    icon={Clock}
                  />
                  <TimeInput
                    label="Fin"
                    value={config.afternoon_end}
                    onChange={(v) => setConfig(prev => ({ ...prev, afternoon_end: v }))}
                    icon={Clock}
                  />
                </div>
                <div className="border-t border-indigo-200 pt-3 mt-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-indigo-700 mb-2">
                    <Coffee className="w-4 h-4" />
                    Récréation après-midi
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <TimeInput
                      label="Début"
                      value={config.afternoon_break_start}
                      onChange={(v) => setConfig(prev => ({ ...prev, afternoon_break_start: v }))}
                      icon={Clock}
                    />
                    <TimeInput
                      label="Fin"
                      value={config.afternoon_break_end}
                      onChange={(v) => setConfig(prev => ({ ...prev, afternoon_break_end: v }))}
                      icon={Clock}
                    />
                  </div>
                </div>
              </div>

              {/* Half-day (Wednesday/Saturday morning) */}
              {(config.has_wednesday_morning || config.has_saturday_morning) && (
                <div className="space-y-3 bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-2 font-semibold text-green-800">
                    <Sun className="w-4 h-4" />
                    Demi-journée (Mer./Sam. matin)
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <TimeInput
                      label="Début"
                      value={config.half_day_start}
                      onChange={(v) => setConfig(prev => ({ ...prev, half_day_start: v }))}
                      icon={Clock}
                    />
                    <TimeInput
                      label="Fin"
                      value={config.half_day_end}
                      onChange={(v) => setConfig(prev => ({ ...prev, half_day_end: v }))}
                      icon={Clock}
                    />
                  </div>
                  <div className="border-t border-green-200 pt-3 mt-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-green-700 mb-2">
                      <Coffee className="w-4 h-4" />
                      Récréation
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <TimeInput
                        label="Début"
                        value={config.half_day_break_start}
                        onChange={(v) => setConfig(prev => ({ ...prev, half_day_break_start: v }))}
                        icon={Clock}
                      />
                      <TimeInput
                        label="Fin"
                        value={config.half_day_break_end}
                        onChange={(v) => setConfig(prev => ({ ...prev, half_day_break_end: v }))}
                        icon={Clock}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Grid interval selector */}
              <div className="space-y-3 bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 font-semibold text-slate-800">
                  <Clock className="w-4 h-4" />
                  Intervalle de la grille
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Définit la précision du découpage horaire pour le cahier-journal
                </p>
                <div className="flex items-center gap-3">
                  <Select 
                    value={config.grid_interval?.toString() || "15"} 
                    onValueChange={(v) => setConfig(prev => ({ ...prev, grid_interval: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 minute</SelectItem>
                      <SelectItem value="2">2 minutes</SelectItem>
                      <SelectItem value="5">5 minutes</SelectItem>
                      <SelectItem value="10">10 minutes</SelectItem>
                      <SelectItem value="15">15 minutes (défaut)</SelectItem>
                      <SelectItem value="20">20 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 heure</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-500">
                    Snapping: {config.grid_interval || 15} min
                  </span>
                </div>
              </div>
            </TabsContent>

            {/* Tab: Profil */}
            <TabsContent value="profil" className="space-y-6">
              {/* Academic Zone */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Zone académique</Label>
                <Select value={profile.zone} onValueChange={(v) => setProfile(prev => ({ ...prev, zone: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {zones.map((zone) => (
                      <SelectItem key={zone.id} value={zone.id}>
                        Zone {zone.id} - {zone.academies.slice(0, 3).join(", ")}
                        {zone.academies.length > 3 && "..."}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  La zone détermine les dates de vacances scolaires
                </p>
              </div>

              {/* Cycles (multi-select for classes like GS-CP) */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Cycle(s) d'enseignement</Label>
                <div className="flex flex-wrap gap-2">
                  {CYCLES.map((cycle) => {
                    const isSelected = profile.cycles.includes(cycle.id);
                    return (
                      <Button
                        key={cycle.id}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setProfile(prev => {
                            const newCycles = isSelected
                              ? prev.cycles.filter(c => c !== cycle.id)
                              : [...prev.cycles, cycle.id];
                            // Keep at least one cycle selected
                            if (newCycles.length === 0) return prev;
                            // Filter niveaux to only include those from selected cycles
                            const validNiveaux = newCycles.flatMap(c => NIVEAUX_BY_CYCLE[c]?.map(n => n.id) || []);
                            const filteredNiveaux = prev.niveaux.filter(n => validNiveaux.includes(n));
                            return { ...prev, cycles: newCycles, niveaux: filteredNiveaux };
                          });
                        }}
                        className="min-w-32"
                      >
                        {cycle.label}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  {profile.cycles.length > 1 
                    ? `Classe à cheval sur ${profile.cycles.length} cycles (ex: GS-CP, CE2-CM1)`
                    : "Sélectionnez plusieurs cycles pour les classes multi-cycles"}
                </p>
              </div>

              {/* Niveaux - show all niveaux from selected cycles */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Niveaux de votre classe</Label>
                <div className="flex flex-wrap gap-2">
                  {profile.cycles.flatMap((cycleId) => 
                    NIVEAUX_BY_CYCLE[cycleId]?.map((niveau) => (
                      <Button
                        key={niveau.id}
                        variant={profile.niveaux.includes(niveau.id) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleNiveau(niveau.id)}
                      >
                        {niveau.label}
                      </Button>
                    )) || []
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {profile.niveaux.length > 1 
                    ? `Classe multi-niveaux : ${profile.niveaux.map(n => n.toUpperCase()).join(" + ")}`
                    : "Sélectionnez vos niveaux"}
                </p>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
